import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CalorieRing } from "@/components/CalorieRing";
import { MacroBar } from "@/components/MacroBar";
import { StreakNudge } from "@/components/StreakNudge";
import { WaterCard } from "@/components/WaterCard";
import { WeeklyRecap } from "@/components/WeeklyRecap";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Camera, Sparkles, MessageCircle, RefreshCw, Plus, Loader2, Sun, Sunset, Moon, Cookie,
} from "lucide-react";

const MEAL_META = {
  breakfast: { label: "Breakfast", icon: Sun },
  lunch: { label: "Lunch", icon: Sunset },
  dinner: { label: "Dinner", icon: Moon },
  snack: { label: "Snacks", icon: Cookie },
};

export default function Dashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);

  const load = () => api.dashboard().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  // Streak nudges: milestone celebration + optional browser reminder (once/day)
  useEffect(() => {
    const s = data?.streak;
    if (!s) return;
    const key = new Date().toISOString().slice(0, 10);
    if (s.milestone && s.logged_today) {
      const mk = `calorify_milestone_${key}_${s.streak}`;
      if (!localStorage.getItem(mk)) {
        localStorage.setItem(mk, "1");
        toast.success(`🔥 ${s.streak}-day streak! You're on fire.`);
      }
    }
    if (s.earned_freeze) {
      const fk = `calorify_freeze_earned_${key}_${s.streak}`;
      if (!localStorage.getItem(fk)) {
        localStorage.setItem(fk, "1");
        toast.success("❄️ You earned a streak freeze! It'll protect a missed day.");
      }
    }
    if (s.at_risk && profile?.notifications?.streak && "Notification" in window && Notification.permission === "granted") {
      const nk = `calorify_streaknudge_${key}`;
      if (!localStorage.getItem(nk)) {
        localStorage.setItem(nk, "1");
        try {
          new Notification("Don't break your streak! 🔥", {
            body: `Your ${s.streak}-day streak is at risk. Log a meal to keep it going.`,
          });
        } catch (e) {}
      }
    }

    // Smart evening reminder: only nudge if calories/water still short of goal (after 6pm, once/day)
    const hour = new Date().getHours();
    const notif = profile?.notifications || {};
    if (hour >= 18 && "Notification" in window && Notification.permission === "granted") {
      const goalCal = profile?.calorie_goal || 2000;
      const consumed = data?.totals?.calories || 0;
      const water = data?.water?.glasses || 0;
      const waterGoal = data?.water?.goal || 8;
      const calShort = notif.progress && consumed > 0 && consumed < goalCal * 0.7;
      const waterShort = notif.hydration && water < waterGoal;
      if (calShort || waterShort) {
        const ek = `calorify_evening_${key}`;
        if (!localStorage.getItem(ek)) {
          localStorage.setItem(ek, "1");
          const parts = [];
          if (calShort) parts.push(`${Math.round(goalCal - consumed)} kcal`);
          if (waterShort) parts.push(`${waterGoal - water} glasses of water`);
          try {
            new Notification("Evening check-in 🌙", {
              body: `You're still ${parts.join(" and ")} short of today's goal. A light meal or a glass of water can round out your day.`,
            });
          } catch (e) {}
        }
      }
    }
  }, [data, profile]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  const genPlan = async () => {
    setPlanBusy(true);
    try {
      const exclude = plan ? Object.values(plan).map((v) => v.name).join(", ") : null;
      const p = await api.generatePlan(exclude);
      setPlan(p);
    } catch (e) {
      toast.error(e.message || "Could not generate plan");
    } finally {
      setPlanBusy(false);
    }
  };

  const goal = profile?.calorie_goal || 2000;
  const totals = data?.totals || { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
  const byType = data?.by_type || {};

  return (
    <div className="px-5 pt-8 space-y-7">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight" data-testid="greeting">
            {greeting()}, {(profile?.name || "there").split(" ")[0]}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">Let's make today count.</p>
        </div>
        <button
          onClick={() => navigate("/coach")}
          data-testid="coach-shortcut"
          className="w-11 h-11 rounded-full bg-card border border-border flex items-center justify-center hover:border-primary/40 transition-colors"
        >
          <MessageCircle className="w-5 h-5 text-primary" />
        </button>
      </div>

      {/* streak nudge */}
      <StreakNudge streak={data?.streak} onScan={() => navigate("/scan")} />

      {/* hydration */}
      <WaterCard glasses={data?.water?.glasses || 0} goal={data?.water?.goal || 8} />

      {/* calorie ring */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-3xl bg-card border border-border p-6 flex flex-col items-center"
      >
        <CalorieRing consumed={totals.calories} goal={goal} />
      </motion.div>

      {/* macros */}
      <div className="grid grid-cols-2 gap-3">
        <MacroBar label="Protein" value={totals.protein} goal={profile?.protein_goal || 120} color="#4A5D23" testid="macro-protein" />
        <MacroBar label="Carbs" value={totals.carbs} goal={profile?.carb_goal || 220} color="#E69C24" testid="macro-carbs" />
        <MacroBar label="Fat" value={totals.fat} goal={profile?.fat_goal || 65} color="#C45A39" testid="macro-fat" />
        <MacroBar label="Fiber" value={totals.fiber} goal={profile?.fiber_goal || 30} color="#8B7355" testid="macro-fiber" />
      </div>

      {/* scan CTA */}
      <button
        onClick={() => navigate("/scan")}
        data-testid="dashboard-scan-btn"
        className="w-full rounded-3xl bg-primary text-primary-foreground p-5 flex items-center gap-4 hover:bg-primary/90 transition-colors active:scale-[0.99]"
      >
        <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
          <Camera className="w-6 h-6" />
        </div>
        <div className="text-left flex-1">
          <div className="font-semibold text-base">Scan your food</div>
          <div className="text-sm opacity-80">Snap a photo, get instant AI nutrition</div>
        </div>
        <Sparkles className="w-5 h-5 opacity-80" />
      </button>

      {/* today's nutrition */}
      <div className="space-y-3">
        <h2 className="font-display text-xl font-medium tracking-tight">Today's Nutrition</h2>
        {["breakfast", "lunch", "dinner", "snack"].map((mt) => {
          const meals = byType[mt] || [];
          const cals = meals.reduce((s, m) => s + Number(m.calories || 0), 0);
          const M = MEAL_META[mt];
          return (
            <div key={mt} data-testid={`meal-section-${mt}`} className="rounded-2xl bg-card border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <M.icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{M.label}</div>
                  {meals.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Nothing logged yet.</div>
                  ) : (
                    <div className="text-xs text-muted-foreground">{meals.map((m) => m.meal_name).join(", ")}</div>
                  )}
                </div>
                {meals.length > 0 ? (
                  <span className="text-sm font-display font-medium tabular-nums">{Math.round(cals)} kcal</span>
                ) : (
                  <button
                    onClick={() => navigate("/scan")}
                    className="w-8 h-8 rounded-full border border-dashed border-primary/40 flex items-center justify-center text-primary hover:bg-primary/5 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* daily plan */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-medium tracking-tight">Your Plan for Today</h2>
          <button onClick={genPlan} disabled={planBusy} data-testid="generate-plan-btn" className="text-sm text-primary font-semibold flex items-center gap-1 disabled:opacity-50">
            {planBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {plan ? "Another" : "Generate"}
          </button>
        </div>
        {!plan && !planBusy && (
          <div className="rounded-2xl border border-dashed border-border p-6 text-center">
            <p className="text-sm text-muted-foreground">Tap Generate for AI meal ideas tailored to your goals.</p>
          </div>
        )}
        {planBusy && !plan && (
          <div className="rounded-2xl bg-card border border-border p-6 flex items-center justify-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Crafting your plan…
          </div>
        )}
        {plan && (
          <div className="space-y-2">
            {["breakfast", "lunch", "snack", "dinner"].map((k) =>
              plan[k] ? (
                <div key={k} className="rounded-2xl bg-card border border-border p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="font-semibold text-sm mt-0.5">{plan[k].name}</div>
                  </div>
                  <span className="text-sm font-display font-medium tabular-nums text-primary">{Math.round(plan[k].calories)} kcal</span>
                </div>
              ) : null
            )}
          </div>
        )}
      </div>

      {/* weekly recap */}
      <WeeklyRecap />

      <div className="h-2" />
    </div>
  );
}
